/**
 * sidebar/icons.tsx — Inline 16x16 stroked SVG icons for the sidebar tree.
 * stroke=currentColor, strokeWidth=1.5, fill=none. No emojis, no external libs.
 * Named identifiers match `icon` strings in lib/sidebar-tree.ts.
 */
import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement>;

const base: SVGProps<SVGSVGElement> = {
	viewBox: "0 0 16 16",
	stroke: "currentColor",
	strokeWidth: 1.5,
	fill: "none",
	strokeLinecap: "round",
	strokeLinejoin: "round",
	xmlns: "http://www.w3.org/2000/svg",
	"aria-hidden": true,
};

export function Target(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<circle cx="8" cy="8" r="6" />
			<circle cx="8" cy="8" r="3" />
			<circle cx="8" cy="8" r="1" />
		</svg>
	);
}

export function Megaphone(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M3 6v4l8 3V3L3 6z" />
			<path d="M3 6H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h1" />
			<path d="M5 11v1.5a1.5 1.5 0 0 0 3 0V12" />
		</svg>
	);
}

export function Check(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M3 8.5L6.5 12 13 4" />
		</svg>
	);
}

export function Users(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<circle cx="6" cy="6" r="2.5" />
			<path d="M2 13c0-2 2-3.5 4-3.5S10 11 10 13" />
			<path d="M10.5 5a2 2 0 0 1 0 4" />
			<path d="M12 13c0-1.5-1-3-2.5-3.5" />
		</svg>
	);
}

export function Briefcase(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<rect x="2" y="5" width="12" height="9" rx="1.5" />
			<path d="M6 5V3.5A1.5 1.5 0 0 1 7.5 2h1A1.5 1.5 0 0 1 10 3.5V5" />
			<path d="M2 9h12" />
		</svg>
	);
}

export function Image(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<rect x="2" y="3" width="12" height="10" rx="1.5" />
			<circle cx="6" cy="7" r="1.25" />
			<path d="M14 11L10.5 7.5 4 13" />
		</svg>
	);
}

export function BarChart(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M2 13h12" />
			<path d="M4 13V9" />
			<path d="M7 13V6" />
			<path d="M10 13V8" />
			<path d="M13 13V4" />
		</svg>
	);
}

export function Shield(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M8 2l5 2v4c0 3-2 5-5 6-3-1-5-3-5-6V4l5-2z" />
		</svg>
	);
}

export function Scale(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M8 2v12" />
			<path d="M4 14h8" />
			<path d="M3 5h10" />
			<path d="M5 5L3 10h4L5 5z" />
			<path d="M11 5l-2 5h4l-2-5z" />
		</svg>
	);
}

export function Plug(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M5 2v3M11 2v3" />
			<path d="M3 5h10v3a5 5 0 0 1-10 0V5z" />
			<path d="M8 13v2" />
		</svg>
	);
}

export function Smartphone(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<rect x="4" y="1.5" width="8" height="13" rx="1.5" />
			<path d="M7 12h2" />
		</svg>
	);
}

export function CreditCard(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<rect x="1.5" y="4" width="13" height="9" rx="1.5" />
			<path d="M1.5 7h13" />
			<path d="M4 10.5h2.5" />
		</svg>
	);
}

export function Gear(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<circle cx="8" cy="8" r="2" />
			<path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.5 3.5l1.5 1.5M11 11l1.5 1.5M3.5 12.5L5 11M11 5l1.5-1.5" />
		</svg>
	);
}

export function Code(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M5 4L1 8l4 4" />
			<path d="M11 4l4 4-4 4" />
			<path d="M9 3L7 13" />
		</svg>
	);
}

export function Chevron(p: IconProps) {
	// Right-pointing; rotate 90deg via transform to indicate "expanded".
	return (
		<svg {...base} {...p}>
			<path d="M6 4l4 4-4 4" />
		</svg>
	);
}

export function Star(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M8 2l1.8 3.7 4.1.6-3 2.9.7 4.1L8 11.4l-3.6 1.9.7-4.1-3-2.9 4.1-.6L8 2z" />
		</svg>
	);
}

export function Search(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<circle cx="7" cy="7" r="4.5" />
			<path d="M10.5 10.5L14 14" />
		</svg>
	);
}

export function Layers(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M8 2L2 5l6 3 6-3-6-3z" />
			<path d="M2 8l6 3 6-3" />
			<path d="M2 11l6 3 6-3" />
		</svg>
	);
}

export function Home(p: IconProps) {
	return (
		<svg {...base} {...p}>
			<path d="M2 7l6-5 6 5v6a1 1 0 0 1-1 1h-3v-4H6v4H3a1 1 0 0 1-1-1V7z" />
		</svg>
	);
}

/** Map icon-name string (from sidebar-tree.ts) -> component. */
export const ICON_BY_NAME: Record<
	string,
	(p: IconProps) => ReturnType<typeof Target>
> = {
	target: Target,
	megaphone: Megaphone,
	check: Check,
	users: Users,
	briefcase: Briefcase,
	image: Image,
	"bar-chart": BarChart,
	shield: Shield,
	scale: Scale,
	plug: Plug,
	smartphone: Smartphone,
	"credit-card": CreditCard,
	gear: Gear,
	code: Code,
	layers: Layers,
	home: Home,
	star: Star,
};
